import React, { useState, useMemo } from "react";
import {
  useListChartOfAccounts, useCreateChartOfAccount, useUpdateChartOfAccount, useDeleteChartOfAccount,
  useListAccountMappings, useUpsertAccountMapping, useApplyDefaultAccountMappings,
  useListJournalEntries, useCreateJournalEntry, useUpdateJournalEntry, usePostJournalEntry, useVoidJournalEntry,
  useListGlEntries, useGetTrialBalance, useGetBalanceSheet, useGetIncomeStatement,
  useGetAccountStatement, useGetPartyStatement, useListProjects,
  useListCustomers, useListMasterSCs, useListMasterSuppliers, useListMasterStaff, useListExpenseCategories,
  getListChartOfAccountsQueryKey, getListAccountMappingsQueryKey, getListJournalEntriesQueryKey, getListGlEntriesQueryKey,
  type ChartOfAccount, type AccountMapping, type JournalEntry, type JournalEntryLine,
  type GlEntry, type TrialBalanceRow, type BalanceSheet, type BalanceSheetItem,
  type IncomeStatement, type IncomeStatementLineItem, type AccountStatement,
  type PartyStatement, type PartyStatementAccount,
  type CreateChartOfAccountBody, type UpsertAccountMappingBody,
  type CreateJournalEntryBody, type UpdateJournalEntryBody,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  BookOpen, Map as MapIcon, FileText, Layers, Scale, BarChart3, TrendingUp,
  ChevronDown, ChevronRight, ChevronLeft, Menu, Plus, Pencil, Trash2, Check,
  Search, Filter, Download, X, CreditCard, Users,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

function uid(): string {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36);
}

const N = (v: unknown) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fc = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const GROUPS = [
  {
    label: "Setup",
    items: [
      { id: "coa", label: "Chart of Accounts", icon: BookOpen },
      { id: "mapping", label: "Transaction Mapping", icon: MapIcon },
    ],
  },
  {
    label: "Entries",
    items: [
      { id: "journal", label: "Journal Entries", icon: FileText },
      { id: "gl", label: "GL Entries", icon: Layers },
    ],
  },
  {
    label: "Reports",
    items: [
      { id: "trial", label: "Trial Balance", icon: Scale },
      { id: "bs", label: "Balance Sheet", icon: BarChart3 },
      { id: "is", label: "Income Statement", icon: TrendingUp },
      { id: "acct-stmt", label: "Account Statement", icon: CreditCard },
      { id: "party-stmt", label: "Party Statement", icon: Users },
    ],
  },
];

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  collapsed?: boolean;
  onSelect?: () => void;
}

function SidebarNav({ activeTab, setActiveTab, collapsed, onSelect }: SidebarProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (label: string) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="space-y-1">
      {GROUPS.map(group => {
        const isExpanded = expandedGroups[group.label] ?? false;
        return (
          <div key={group.label}>
            <button
              onClick={() => toggleGroup(group.label)}
              className={cn(
                "w-full flex items-center gap-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors",
                collapsed ? "px-0 py-2 justify-center" : "px-3 py-2"
              )}
            >
              {!collapsed && <span className="flex-1 text-left">{group.label}</span>}
              {collapsed ? (
                <span className="text-[9px]">{group.label.slice(0, 1)}</span>
              ) : (
                isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
              )}
            </button>
            {(isExpanded || collapsed) && group.items.map(item => {
              const active = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  title={collapsed ? item.label : undefined}
                  onClick={() => { setActiveTab(item.id); onSelect?.(); }}
                  className={cn(
                    "w-full flex items-center gap-3 rounded-xl text-sm font-medium transition-all duration-200",
                    collapsed ? "px-0 py-3 justify-center" : "px-4 py-2.5",
                    active
                      ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

interface AccountingModuleProps {
  companyId: string;
  companyName?: string;
  plan?: string;
}

export default function AccountingModule({ companyId, companyName, plan }: AccountingModuleProps) {
  const [activeTab, setActiveTab] = useState("coa");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { data: projects = [] } = useListProjects({ companyId });
  const projectsList = projects as Array<{ id: string; name: string }>;

  const renderTab = () => {
    switch (activeTab) {
      case "coa": return <ChartOfAccountsTab companyId={companyId} />;
      case "mapping": return <MappingTab companyId={companyId} plan={plan} />;
      case "journal": return <JournalEntriesTab companyId={companyId} projects={projectsList} />;
      case "gl": return <GlEntriesTab companyId={companyId} />;
      case "trial": return <TrialBalanceTab companyId={companyId} />;
      case "bs": return <BalanceSheetTab companyId={companyId} />;
      case "is": return <IncomeStatementTab companyId={companyId} />;
      case "acct-stmt": return <AccountStatementTab companyId={companyId} />;
      case "party-stmt": return <PartyStatementTab companyId={companyId} />;
      default: return <ChartOfAccountsTab companyId={companyId} />;
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] bg-background rounded-xl border border-border/50 overflow-hidden">
      <aside
        className="hidden md:flex bg-card border-r border-border/50 flex-col shrink-0 overflow-hidden"
        style={{ width: sidebarCollapsed ? 64 : 256, transition: "width 200ms linear" }}
      >
        <div className={cn("flex items-center border-b border-border/50 shrink-0", sidebarCollapsed ? "p-3 justify-center" : "p-4 gap-2")}>
          <BookOpen className="w-5 h-5 text-primary shrink-0" />
          {!sidebarCollapsed && <span className="font-bold text-sm whitespace-nowrap">Accounting</span>}
        </div>
        <nav className={cn("flex-1 py-4 space-y-0.5 overflow-y-auto no-scrollbar", sidebarCollapsed ? "px-2" : "px-3")}>
          <SidebarNav activeTab={activeTab} setActiveTab={setActiveTab} collapsed={sidebarCollapsed} />
        </nav>
        <div className={cn("pb-4 flex", sidebarCollapsed ? "justify-center" : "px-3 justify-end")}>
          <button
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 bg-card border-b border-border/50 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors">
                  <Menu className="w-4 h-4 text-muted-foreground" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="p-5 border-b border-border">
                  <SheetTitle className="text-left flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-primary" /> Accounting
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                  <SidebarNav activeTab={activeTab} setActiveTab={setActiveTab} onSelect={() => setMobileNavOpen(false)} />
                </nav>
              </SheetContent>
            </Sheet>
            <h2 className="text-sm font-semibold">
              {GROUPS.flatMap(g => g.items).find(i => i.id === activeTab)?.label ?? "Accounting"}
            </h2>
          </div>
        </header>
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {renderTab()}
        </div>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ CHART OF ACCOUNTS */
function ChartOfAccountsTab({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState("");
  const queryClient = useQueryClient();
  const { data: accounts = [], isLoading } = useListChartOfAccounts({ companyId });
  const createMutation = useCreateChartOfAccount();
  const updateMutation = useUpdateChartOfAccount();
  const deleteMutation = useDeleteChartOfAccount();
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAccount, setEditAccount] = useState<ChartOfAccount | null>(null);
  const [form, setForm] = useState({ code: "", name: "", accountType: "asset", subtype: "", parentId: "", normalBalance: "debit", linkedEntityType: "", description: "" });

  const filtered = useMemo(() => {
    let list = accounts;
    if (typeFilter !== "all") list = list.filter(a => a.accountType === typeFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(a => a.code.toLowerCase().includes(s) || a.name.toLowerCase().includes(s));
    }
    return list;
  }, [accounts, typeFilter, search]);

  const tree = useMemo(() => {
    const map = new Map<string | null, typeof filtered>();
    for (const a of filtered) {
      const pid = a.parentId || null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid)!.push(a);
    }
    return map;
  }, [filtered]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const renderTree = (parentId: string | null, depth: number): JSX.Element[] => {
    const children = tree.get(parentId) || [];
    const rows: JSX.Element[] = [];
    for (const a of children) {
      const hasChildren = tree.has(a.id);
      const isOpen = expanded.has(a.id);
      rows.push(
        <TableRow key={a.id} className={!a.isActive ? "opacity-50" : ""}>
          <TableCell className="font-mono text-xs" style={{ paddingLeft: `${depth * 24 + 12}px` }}>
            <span className="inline-flex items-center gap-1">
              {hasChildren && (
                <button onClick={() => toggleExpand(a.id)} className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground">
                  {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
              )}
              {!hasChildren && <span className="w-4" />}
              {a.code}
            </span>
          </TableCell>
          <TableCell>
            <span className="text-sm">{a.name}</span>
            {a.isGroup && <Badge variant="outline" className="ml-2 text-[10px]">Group</Badge>}
          </TableCell>
          <TableCell><Badge variant="secondary" className="text-[10px] capitalize">{a.accountType}</Badge></TableCell>
          <TableCell className="text-xs text-muted-foreground">{a.subtype.replace(/_/g, " ")}</TableCell>
          <TableCell>
            <Badge variant={a.normalBalance === "debit" ? "default" : "outline"} className="text-[10px]">
              {a.normalBalance === "debit" ? "Dr" : "Cr"}
            </Badge>
          </TableCell>
          <TableCell className="text-xs text-muted-foreground">{a.linkedEntityType || "—"}</TableCell>
          <TableCell>
            <div className="flex items-center gap-1">
              {!a.isGroup && (
                <>
                  <Switch
                    checked={a.isActive}
                    onCheckedChange={async (checked) => {
                      await updateMutation.mutateAsync({ id: a.id, data: { isActive: checked } });
                      queryClient.invalidateQueries({ queryKey: getListChartOfAccountsQueryKey({ companyId }) });
                    }}
                    className="scale-75"
                  />
                  <button
                    onClick={() => {
                      setEditAccount(a);
                      setForm({ code: a.code, name: a.name, accountType: a.accountType, subtype: a.subtype, parentId: a.parentId || "", normalBalance: a.normalBalance, linkedEntityType: a.linkedEntityType || "", description: a.description || "" });
                      setDialogOpen(true);
                    }}
                    className="p-1 rounded hover:bg-muted"
                  >
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </button>
                  {!a.isSystem && (
                    <button
                      onClick={async () => {
                        if (!confirm("Delete this account?")) return;
                        try {
                          await deleteMutation.mutateAsync({ id: a.id });
                          queryClient.invalidateQueries({ queryKey: getListChartOfAccountsQueryKey({ companyId }) });
                          toast({ title: "Account deleted" });
                        } catch (e: unknown) {
                          toast({ title: "Error", description: e instanceof Error ? e.message : "Unknown error", variant: "destructive" });
                        }
                      }}
                      className="p-1 rounded hover:bg-muted"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </button>
                  )}
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
      if (hasChildren && isOpen) {
        rows.push(...renderTree(a.id, depth + 1));
      }
    }
    return rows;
  };

  const groupAccounts = accounts.filter(a => a.isGroup);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search code or name…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 rounded-lg" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[140px] h-9 rounded-lg">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="asset">Asset</SelectItem>
              <SelectItem value="liability">Liability</SelectItem>
              <SelectItem value="equity">Equity</SelectItem>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          className="rounded-lg"
          onClick={() => {
            setEditAccount(null);
            setForm({ code: "", name: "", accountType: "asset", subtype: "", parentId: "", normalBalance: "debit", linkedEntityType: "", description: "" });
            setDialogOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-1" /> Add Account
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Card className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead className="w-[140px]">Subtype</TableHead>
                <TableHead className="w-[60px]">Dr/Cr</TableHead>
                <TableHead className="w-[120px]">Linked</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTree(null, 0)}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No accounts found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editAccount ? "Edit Account" : "Add Account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Code</Label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} className="h-9 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-9 rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Account Type</Label>
                <Select value={form.accountType} onValueChange={v => setForm(f => ({ ...f, accountType: v }))}>
                  <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["asset", "liability", "equity", "income", "expense"].map(t => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Normal Balance</Label>
                <Select value={form.normalBalance} onValueChange={v => setForm(f => ({ ...f, normalBalance: v }))}>
                  <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Subtype</Label>
                <Input value={form.subtype} onChange={e => setForm(f => ({ ...f, subtype: e.target.value }))} className="h-9 rounded-lg" placeholder="e.g. receivable" />
              </div>
              <div>
                <Label className="text-xs">Linked Entity</Label>
                <Select value={form.linkedEntityType || "_none"} onValueChange={v => setForm(f => ({ ...f, linkedEntityType: v === "_none" ? "" : v }))}>
                  <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="expense_category">Expense Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Parent Group</Label>
              <Select value={form.parentId || "_none"} onValueChange={v => setForm(f => ({ ...f, parentId: v === "_none" ? "" : v }))}>
                <SelectTrigger className="h-9 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None (root)</SelectItem>
                  {groupAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-9 rounded-lg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button
              className="rounded-lg"
              onClick={async () => {
                try {
                  if (editAccount) {
                    await updateMutation.mutateAsync({ id: editAccount.id, data: form });
                  } else {
                    await createMutation.mutateAsync({ data: form as CreateChartOfAccountBody });
                  }
                  queryClient.invalidateQueries({ queryKey: getListChartOfAccountsQueryKey({ companyId }) });
                  setDialogOpen(false);
                  toast({ title: editAccount ? "Account updated" : "Account created" });
                } catch (e: unknown) {
                  toast({ title: "Error", description: e instanceof Error ? e.message : "Unknown error", variant: "destructive" });
                }
              }}
            >
              {editAccount ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ MAPPING */
type MappingItem = {
  key: string;
  label: string;
  hint: string;
  hasVat?: boolean;
  drSubtypes?: string[];
  crSubtypes?: string[];
  vatSubtypes?: string[];
};
type MappingGroup = { label: string; icon: React.ReactNode; color: string; items: MappingItem[] };

const MAPPING_GROUPS: MappingGroup[] = [
  {
    label: "Client Invoicing",
    icon: <FileText className="w-4 h-4" />,
    color: "text-blue-600 bg-blue-50",
    items: [
      { key: "Client Invoice",                      label: "Revenue — Progress / Final Bill",   hint: "DR Receivable · CR Contract Revenue",
        drSubtypes: ["receivable"],                 crSubtypes: ["direct_income","indirect_income"], vatSubtypes: ["tax"], hasVat: true },
      { key: "Client Invoice — Retention",          label: "Retention Withheld by Client",      hint: "DR Retention Receivable · CR Trade Receivable",
        drSubtypes: ["receivable"],                 crSubtypes: ["receivable"] },
      { key: "Client Invoice — Retention Release",  label: "Retention Released by Client",      hint: "DR Trade Receivable · CR Retention Receivable",
        drSubtypes: ["receivable"],                 crSubtypes: ["receivable"] },
      { key: "Client Invoice — Advance",            label: "Client Advance Received",           hint: "DR Bank · CR Advance from Clients",
        drSubtypes: ["bank"],                       crSubtypes: ["advance_receipt"] },
      { key: "Client Invoice — Advance Recovery",   label: "Advance Recovery (deducted)",       hint: "DR Advance from Clients · CR Trade Receivable",
        drSubtypes: ["advance_receipt"],            crSubtypes: ["receivable"] },
      { key: "Client Invoice — Credit Note",        label: "Credit Note to Client",             hint: "DR Contract Revenue · CR Trade Receivable",
        drSubtypes: ["direct_income","indirect_income"], crSubtypes: ["receivable"] },
      { key: "Client Invoice — Deduction",          label: "Deduction / Penalty from Client",   hint: "DR Contract Revenue · CR Trade Receivable",
        drSubtypes: ["direct_income","indirect_income"], crSubtypes: ["receivable"] },
    ],
  },
  {
    label: "Client Receipts",
    icon: <CreditCard className="w-4 h-4" />,
    color: "text-green-600 bg-green-50",
    items: [
      { key: "Client Receipt",                      label: "Standard Payment Receipt",          hint: "DR Bank · CR Trade Receivable",
        drSubtypes: ["bank"],                       crSubtypes: ["receivable"] },
      { key: "Client Receipt — Retention Release",  label: "Retention Release Receipt",         hint: "DR Bank · CR Retention Receivable",
        drSubtypes: ["bank"],                       crSubtypes: ["receivable"] },
      { key: "Client Receipt — Advance",            label: "Advance Payment Receipt",           hint: "DR Bank · CR Advance from Clients",
        drSubtypes: ["bank"],                       crSubtypes: ["advance_receipt"] },
    ],
  },
  {
    label: "Subcontractor Invoicing",
    icon: <FileText className="w-4 h-4" />,
    color: "text-orange-600 bg-orange-50",
    items: [
      { key: "SC Invoice",                          label: "SC Cost — Progress / Final Bill",   hint: "DR Subcontract Costs · CR SC Payable",
        drSubtypes: ["cogs","direct_expense"],      crSubtypes: ["payable"], vatSubtypes: ["tax"], hasVat: true },
      { key: "SC Invoice — Retention",              label: "Retention Withheld from SC",        hint: "DR SC Payable · CR Retention Payable",
        drSubtypes: ["payable"],                    crSubtypes: ["retention"] },
      { key: "SC Invoice — Retention Release",      label: "Retention Released to SC",          hint: "DR Retention Payable · CR SC Payable",
        drSubtypes: ["retention"],                  crSubtypes: ["payable"] },
      { key: "SC Invoice — Advance",                label: "Advance Paid to SC",                hint: "DR Advance to SCs · CR Bank",
        drSubtypes: ["advance"],                    crSubtypes: ["bank"] },
      { key: "SC Invoice — Advance Recovery",       label: "Advance Recovery (deducted from SC)",hint: "DR SC Payable · CR Advance to SCs",
        drSubtypes: ["payable"],                    crSubtypes: ["advance"] },
      { key: "SC Invoice — Credit Note",            label: "Credit Note from SC",               hint: "DR SC Payable · CR Subcontract Costs",
        drSubtypes: ["payable"],                    crSubtypes: ["cogs","direct_expense"] },
      { key: "SC Invoice — Deduction",              label: "Deduction / Penalty to SC",         hint: "DR SC Payable · CR Subcontract Costs",
        drSubtypes: ["payable"],                    crSubtypes: ["cogs","direct_expense"] },
    ],
  },
  {
    label: "Subcontractor Payments",
    icon: <CreditCard className="w-4 h-4" />,
    color: "text-amber-600 bg-amber-50",
    items: [
      { key: "SC Payment",                          label: "Standard Payment to SC",            hint: "DR SC Payable · CR Bank",
        drSubtypes: ["payable"],                    crSubtypes: ["bank"] },
      { key: "SC Payment — Retention Release",      label: "Retention Release Payment to SC",   hint: "DR Retention Payable · CR Bank",
        drSubtypes: ["retention"],                  crSubtypes: ["bank"] },
      { key: "SC Payment — Advance",                label: "Advance Payment to SC",             hint: "DR Advance to SCs · CR Bank",
        drSubtypes: ["advance"],                    crSubtypes: ["bank"] },
    ],
  },
  {
    label: "Supplier Invoicing",
    icon: <FileText className="w-4 h-4" />,
    color: "text-violet-600 bg-violet-50",
    items: [
      { key: "Supplier Invoice",                    label: "Supplier Expense / Purchase",       hint: "DR Supplier Expense · CR Supplier Payable",
        drSubtypes: ["direct_expense","cogs","indirect_expense"], crSubtypes: ["payable"], vatSubtypes: ["tax"], hasVat: true },
      { key: "Supplier Invoice — Credit Note",      label: "Credit Note from Supplier",         hint: "DR Supplier Payable · CR Supplier Expense",
        drSubtypes: ["payable"],                    crSubtypes: ["direct_expense","cogs","indirect_expense"] },
      { key: "Supplier Invoice — Deduction",        label: "Deduction from Supplier",           hint: "DR Supplier Payable · CR Supplier Expense",
        drSubtypes: ["payable"],                    crSubtypes: ["direct_expense","cogs","indirect_expense"] },
    ],
  },
  {
    label: "Supplier Payments",
    icon: <CreditCard className="w-4 h-4" />,
    color: "text-rose-600 bg-rose-50",
    items: [
      { key: "Supplier Payment",                    label: "Payment to Supplier",               hint: "DR Supplier Payable · CR Bank",
        drSubtypes: ["payable"],                    crSubtypes: ["bank"] },
    ],
  },
  {
    label: "Payroll",
    icon: <Users className="w-4 h-4" />,
    color: "text-teal-600 bg-teal-50",
    items: [
      { key: "Payroll",                             label: "Payroll — Monthly Summary",         hint: "DR Payroll Expense · CR Salaries Payable",
        drSubtypes: ["direct_expense","indirect_expense","cogs"], crSubtypes: ["employee","accrued"] },
      { key: "Payroll — Basic Salary",              label: "Basic Salary",                      hint: "DR Payroll Expense · CR Salaries Payable",
        drSubtypes: ["direct_expense","indirect_expense","cogs"], crSubtypes: ["employee","accrued"] },
      { key: "Payroll — Housing Allowance",         label: "Housing Allowance",                 hint: "DR Payroll Expense · CR Salaries Payable",
        drSubtypes: ["direct_expense","indirect_expense","cogs"], crSubtypes: ["employee","accrued"] },
      { key: "Payroll — Transport Allowance",       label: "Transport Allowance",               hint: "DR Payroll Expense · CR Salaries Payable",
        drSubtypes: ["direct_expense","indirect_expense","cogs"], crSubtypes: ["employee","accrued"] },
      { key: "Payroll — Other Allowance",           label: "Other Allowances",                  hint: "DR Payroll Expense · CR Salaries Payable",
        drSubtypes: ["direct_expense","indirect_expense","cogs"], crSubtypes: ["employee","accrued"] },
      { key: "Payroll — Deduction",                 label: "Deductions (GOSI / Tax)",           hint: "DR Salaries Payable · CR GOSI / Tax Payable",
        drSubtypes: ["employee","accrued"],         crSubtypes: ["employee","tax"] },
      { key: "Payroll — Net Pay",                   label: "Net Pay Disbursement",              hint: "DR Salaries Payable · CR Bank",
        drSubtypes: ["employee","accrued"],         crSubtypes: ["bank"] },
    ],
  },
  {
    label: "Direct Expense",
    icon: <TrendingUp className="w-4 h-4" />,
    color: "text-slate-600 bg-slate-100",
    items: [
      { key: "Direct Expense",                      label: "Direct Expense / Petty Cash",       hint: "DR Direct Expense · CR Cash / Bank",
        drSubtypes: ["direct_expense","cogs","indirect_expense"], crSubtypes: ["bank"], vatSubtypes: ["tax"], hasVat: true },
    ],
  },
];

/* account type metadata for chip colors */
function acctTypeColor(code: string): string {
  const n = parseInt(code, 10);
  if (n >= 1000 && n < 2000) return "bg-blue-100 text-blue-800 border-blue-200";
  if (n >= 2000 && n < 3000) return "bg-rose-100 text-rose-800 border-rose-200";
  if (n >= 3000 && n < 4000) return "bg-purple-100 text-purple-800 border-purple-200";
  if (n >= 4000 && n < 5000) return "bg-green-100 text-green-800 border-green-200";
  if (n >= 5000)              return "bg-orange-100 text-orange-800 border-orange-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function AccountChip({ accountId, accounts, label }: { accountId: string; accounts: ChartOfAccount[]; label: string }) {
  const a = accounts.find(ac => ac.id === accountId);
  if (!a) return <span className="text-xs text-muted-foreground italic">Not set</span>;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-mono font-medium ${acctTypeColor(a.code)}`}>
        {a.code} <span className="font-sans font-normal">— {a.name}</span>
      </span>
    </div>
  );
}

function GroupedAccountSelect({
  value, onChange, accounts, placeholder, allowNone, subtypes,
}: {
  value: string; onChange: (v: string) => void; accounts: ChartOfAccount[]; placeholder: string; allowNone?: boolean; subtypes?: string[];
}) {
  const filtered = subtypes?.length ? accounts.filter(a => subtypes.includes(a.subtype ?? "")) : accounts;
  const groups = [
    { label: "🏦 Assets",      types: ["asset"] },
    { label: "📋 Liabilities", types: ["liability"] },
    { label: "💼 Equity",      types: ["equity"] },
    { label: "💰 Income",      types: ["income"] },
    { label: "💸 Expenses",    types: ["expense"] },
  ];
  return (
    <Select value={allowNone ? (value || "_none") : value} onValueChange={v => onChange(allowNone && v === "_none" ? "" : v)}>
      <SelectTrigger className="h-9 rounded-lg text-xs w-full"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-72">
        {allowNone && <SelectItem value="_none" className="text-xs italic text-muted-foreground">None / Not applicable</SelectItem>}
        {groups.map(g => {
          const items = filtered.filter(a => g.types.includes(a.accountType ?? "")).sort((a, b) => a.code.localeCompare(b.code));
          if (items.length === 0) return null;
          return (
            <SelectGroup key={g.label}>
              <SelectLabel className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{g.label}</SelectLabel>
              {items.map(a => (
                <SelectItem key={a.id} value={a.id} className="text-xs font-mono">
                  <span className="font-bold">{a.code}</span> <span className="font-sans font-normal">— {a.name}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-muted-foreground italic">No matching accounts found</div>
        )}
      </SelectContent>
    </Select>
  );
}

function MappingTab({ companyId, plan }: { companyId: string; plan?: string }) {
  const isProPlus = plan === "pro-plus";
  const { data: mappings = [], isLoading: mappingsLoading } = useListAccountMappings({ companyId });
  const { data: accounts = [] } = useListChartOfAccounts({ companyId });
  const { data: expenseCategories = [] } = useListExpenseCategories({ companyId });
  const upsertMutation = useUpsertAccountMapping();
  const applyDefaultsMutation = useApplyDefaultAccountMappings();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editClass, setEditClass] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ debitAccountId: "", creditAccountId: "", vatAccountId: "" });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    MAPPING_GROUPS.forEach(g => { init[g.label] = false; });
    return init;
  });

  const leafAccounts = accounts.filter(a => !a.isGroup && a.isActive);
  const mappingMap = new Map(mappings.map(m => [m.transactionClass, m]));
  const totalItems = MAPPING_GROUPS.reduce((s, g) => s + g.items.length, 0);
  const totalMapped = MAPPING_GROUPS.reduce((s, g) => s + g.items.filter(i => mappingMap.has(i.key)).length, 0);
  const pct = totalItems > 0 ? Math.round((totalMapped / totalItems) * 100) : 0;

  const toggleGroup = (label: string) => setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const applyDefaults = async (overwrite: boolean) => {
    try {
      const result = await applyDefaultsMutation.mutateAsync({ data: { overwrite }, params: { companyId } });
      queryClient.invalidateQueries({ queryKey: getListAccountMappingsQueryKey({ companyId }) });
      toast({ title: overwrite ? "Defaults applied (overwrite)" : "Defaults applied", description: `${result.applied} mappings set, ${result.skipped} skipped.` });
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Header + Apply Defaults ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">
            Define which GL accounts are debited and credited for each transaction type.
            These mappings drive automatic GL posting when you post invoices, receipts, payments, and payroll.
          </p>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden" style={{ maxWidth: 240 }}>
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-medium text-muted-foreground">{totalMapped}/{totalItems} mapped</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg text-xs gap-1.5"
            disabled={applyDefaultsMutation.isPending}
            onClick={() => applyDefaults(false)}
          >
            <Check className="w-3.5 h-3.5" />
            Apply Defaults
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-lg text-xs text-destructive hover:text-destructive gap-1.5"
            disabled={applyDefaultsMutation.isPending}
            onClick={async () => {
              if (!confirm("This will overwrite ALL existing mappings with the recommended defaults. Continue?")) return;
              await applyDefaults(true);
            }}
          >
            <X className="w-3.5 h-3.5" />
            Reset to Defaults
          </Button>
        </div>
      </div>

      {mappingsLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {MAPPING_GROUPS.map(group => {
            const isOpen = expandedGroups[group.label] ?? false;
            const mappedCount = group.items.filter(i => mappingMap.has(i.key)).length;
            const allMapped = mappedCount === group.items.length;
            return (
              <Card key={group.label} className="rounded-xl overflow-hidden border">
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className={`p-1.5 rounded-lg ${group.color}`}>{group.icon}</span>
                    <span>
                      <span className="font-semibold text-sm">{group.label}</span>
                      <span className="text-[11px] text-muted-foreground ml-2">{group.items.length} transaction type{group.items.length > 1 ? "s" : ""}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${allMapped ? "bg-green-100 text-green-700" : mappedCount > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${allMapped ? "bg-green-500" : mappedCount > 0 ? "bg-amber-500" : "bg-slate-300"}`} />
                      {mappedCount}/{group.items.length}
                    </span>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </span>
                </button>

                {/* Items */}
                {isOpen && (
                  <div className="divide-y border-t">
                    {group.items.map(item => {
                      const m = mappingMap.get(item.key);
                      const isEditing = editClass === item.key;
                      const isMapped = !!m;

                      return (
                        <div key={item.key}>
                          {/* Row */}
                          <div className={`flex items-center gap-3 px-4 py-3 ${isEditing ? "bg-muted/30" : "hover:bg-muted/20"} transition-colors`}>
                            {/* Status dot */}
                            <span className={`w-2 h-2 rounded-full shrink-0 ${isMapped ? "bg-green-500" : "bg-slate-200"}`} />

                            {/* Label + hint */}
                            <div className="min-w-0 w-52 shrink-0">
                              <p className="text-sm font-medium leading-tight">{item.label}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{item.hint}</p>
                            </div>

                            {/* Account chips */}
                            {isMapped && !isEditing ? (
                              <div className="flex items-center gap-3 flex-1 flex-wrap min-w-0">
                                <AccountChip accountId={m!.debitAccountId}  accounts={accounts} label="DR" />
                                <AccountChip accountId={m!.creditAccountId} accounts={accounts} label="CR" />
                                {item.hasVat && m!.vatAccountId && (
                                  <AccountChip accountId={m!.vatAccountId} accounts={accounts} label="VAT" />
                                )}
                                {item.hasVat && !m!.vatAccountId && (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">VAT</span>
                                    <span className="text-xs text-muted-foreground italic">None</span>
                                  </div>
                                )}
                              </div>
                            ) : !isEditing ? (
                              <div className="flex-1">
                                <span className="text-xs text-muted-foreground italic">Not configured — click Edit to set up</span>
                              </div>
                            ) : null}

                            {/* Actions */}
                            <div className="ml-auto shrink-0">
                              {!isEditing ? (
                                <Button
                                  size="sm" variant="ghost" className="h-7 px-2 text-xs rounded-lg"
                                  onClick={() => {
                                    setEditClass(item.key);
                                    setEditForm({
                                      debitAccountId: m?.debitAccountId || "",
                                      creditAccountId: m?.creditAccountId || "",
                                      vatAccountId: m?.vatAccountId || "",
                                    });
                                  }}
                                >
                                  <Pencil className="w-3 h-3 mr-1" />{isMapped ? "Edit" : "Set up"}
                                </Button>
                              ) : (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm" variant="ghost" className="h-7 w-7 p-0"
                                    onClick={async () => {
                                      if (!editForm.debitAccountId || !editForm.creditAccountId) {
                                        toast({ title: "Debit and credit accounts are required", variant: "destructive" }); return;
                                      }
                                      await upsertMutation.mutateAsync({
                                        data: { transactionClass: item.key, debitAccountId: editForm.debitAccountId, creditAccountId: editForm.creditAccountId, vatAccountId: editForm.vatAccountId || undefined } as UpsertAccountMappingBody,
                                      });
                                      queryClient.invalidateQueries({ queryKey: getListAccountMappingsQueryKey({ companyId }) });
                                      setEditClass(null);
                                      toast({ title: "Mapping saved" });
                                    }}
                                  >
                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditClass(null)}>
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Inline edit panel */}
                          {isEditing && (
                            <div className="px-4 pb-4 pt-1 bg-muted/20 border-t">
                              <div className={`grid gap-4 ${item.hasVat ? "grid-cols-3" : "grid-cols-2"}`}>
                                <div>
                                  <Label className="text-xs font-semibold mb-1.5 block text-blue-700">Debit Account (DR)</Label>
                                  <GroupedAccountSelect
                                    value={editForm.debitAccountId}
                                    onChange={v => setEditForm(f => ({ ...f, debitAccountId: v }))}
                                    accounts={leafAccounts}
                                    placeholder="Select account to debit…"
                                    subtypes={item.drSubtypes}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs font-semibold mb-1.5 block text-green-700">Credit Account (CR)</Label>
                                  <GroupedAccountSelect
                                    value={editForm.creditAccountId}
                                    onChange={v => setEditForm(f => ({ ...f, creditAccountId: v }))}
                                    accounts={leafAccounts}
                                    placeholder="Select account to credit…"
                                    subtypes={item.crSubtypes}
                                  />
                                </div>
                                {item.hasVat && (
                                  <div>
                                    <Label className="text-xs font-semibold mb-1.5 block text-amber-700">VAT Account (optional)</Label>
                                    <GroupedAccountSelect
                                      value={editForm.vatAccountId}
                                      onChange={v => setEditForm(f => ({ ...f, vatAccountId: v }))}
                                      accounts={leafAccounts}
                                      placeholder="Select VAT account…"
                                      subtypes={item.vatSubtypes}
                                      allowNone
                                    />
                                  </div>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-2 italic">{item.hint}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {/* ── Per-Category Expense Mappings (Direct Expense group only) ── */}
                  {group.label === "Direct Expense" && (
                    <div className={`border-t ${isProPlus ? "" : "opacity-60"}`}>
                      {/* Section header */}
                      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Per-Category Overrides</span>
                          <span className="text-[10px] text-slate-400">— map each expense category to a specific GL account</span>
                        </div>
                        {!isProPlus && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                            🔒 Pro Plus only
                          </span>
                        )}
                      </div>

                      {expenseCategories.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-muted-foreground italic">
                          No expense categories defined yet. Add them in Company Settings → Masters.
                        </div>
                      ) : (
                        expenseCategories.map(cat => {
                          const catKey = `Direct Expense::${cat.id}`;
                          const m = mappingMap.get(catKey);
                          const isCatEditing = editClass === catKey;
                          const isCatMapped = !!m;

                          return (
                            <div key={cat.id}>
                              <div className={`flex items-center gap-3 px-4 py-3 ${isCatEditing ? "bg-muted/30" : "hover:bg-muted/20"} transition-colors`}>
                                {/* Status dot */}
                                <span className={`w-2 h-2 rounded-full shrink-0 ${isCatMapped ? "bg-green-500" : "bg-slate-200"}`} />

                                {/* Category name */}
                                <div className="min-w-0 w-52 shrink-0">
                                  <p className="text-sm font-medium leading-tight">{cat.name}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">DR specific expense account · CR Bank</p>
                                </div>

                                {/* Account chips */}
                                {isCatMapped && !isCatEditing ? (
                                  <div className="flex items-center gap-3 flex-1 flex-wrap min-w-0">
                                    <AccountChip accountId={m!.debitAccountId}  accounts={accounts} label="DR" />
                                    <AccountChip accountId={m!.creditAccountId} accounts={accounts} label="CR" />
                                  </div>
                                ) : !isCatEditing ? (
                                  <div className="flex-1">
                                    <span className="text-xs text-muted-foreground italic">Falls back to default Direct Expense mapping</span>
                                  </div>
                                ) : null}

                                {/* Actions */}
                                <div className="ml-auto shrink-0">
                                  {!isCatEditing ? (
                                    <Button
                                      size="sm" variant="ghost" className="h-7 px-2 text-xs rounded-lg"
                                      disabled={!isProPlus}
                                      onClick={() => {
                                        setEditClass(catKey);
                                        setEditForm({
                                          debitAccountId: m?.debitAccountId || "",
                                          creditAccountId: m?.creditAccountId || "",
                                          vatAccountId: "",
                                        });
                                      }}
                                    >
                                      <Pencil className="w-3 h-3 mr-1" />{isCatMapped ? "Edit" : "Set up"}
                                    </Button>
                                  ) : (
                                    <div className="flex gap-1">
                                      <Button
                                        size="sm" variant="ghost" className="h-7 w-7 p-0"
                                        onClick={async () => {
                                          if (!editForm.debitAccountId || !editForm.creditAccountId) {
                                            toast({ title: "Debit and credit accounts are required", variant: "destructive" }); return;
                                          }
                                          await upsertMutation.mutateAsync({
                                            data: { transactionClass: catKey, debitAccountId: editForm.debitAccountId, creditAccountId: editForm.creditAccountId } as UpsertAccountMappingBody,
                                          });
                                          queryClient.invalidateQueries({ queryKey: getListAccountMappingsQueryKey({ companyId }) });
                                          setEditClass(null);
                                          toast({ title: `Mapping saved for "${cat.name}"` });
                                        }}
                                      >
                                        <Check className="w-3.5 h-3.5 text-green-600" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditClass(null)}>
                                        <X className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Inline edit panel */}
                              {isCatEditing && (
                                <div className="px-4 pb-4 pt-1 bg-muted/20 border-t">
                                  <div className="grid gap-4 grid-cols-2">
                                    <div>
                                      <Label className="text-xs font-semibold mb-1.5 block text-blue-700">Debit Account (DR) — Expense</Label>
                                      <GroupedAccountSelect
                                        value={editForm.debitAccountId}
                                        onChange={v => setEditForm(f => ({ ...f, debitAccountId: v }))}
                                        accounts={leafAccounts}
                                        placeholder="Select expense account…"
                                        subtypes={["direct_expense","cogs","indirect_expense"]}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs font-semibold mb-1.5 block text-green-700">Credit Account (CR) — Bank / Cash</Label>
                                      <GroupedAccountSelect
                                        value={editForm.creditAccountId}
                                        onChange={v => setEditForm(f => ({ ...f, creditAccountId: v }))}
                                        accounts={leafAccounts}
                                        placeholder="Select bank / cash account…"
                                        subtypes={["bank"]}
                                      />
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-muted-foreground mt-2 italic">
                                    This overrides the default Direct Expense mapping specifically for the <strong>{cat.name}</strong> category.
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    )}
  </div>
);
}

/* ═══════════════════════════════════════════════════════ JOURNAL ENTRIES */
function JournalEntriesTab({ companyId, projects }: { companyId: string; projects: Array<{ id: string; name: string }> }) {
  const { data: entries = [], isLoading } = useListJournalEntries({ companyId });
  const { data: accounts = [] } = useListChartOfAccounts({ companyId });
  const { data: customers = [] }      = useListCustomers({ companyId });
  const { data: masterSCs = [] }      = useListMasterSCs({ companyId });
  const { data: masterSuppliers = [] } = useListMasterSuppliers({ companyId });
  const { data: masterStaff = [] }    = useListMasterStaff({ companyId });
  const createMutation = useCreateJournalEntry();
  const updateMutation = useUpdateJournalEntry();
  const postMutation = usePostJournalEntry();
  const voidMutation = useVoidJournalEntry();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewEntry, setViewEntry] = useState<JournalEntry | null>(null);
  const [form, setForm] = useState({ entryDate: new Date().toISOString().split("T")[0], reference: "", description: "" });
  const [lines, setLines] = useState<{ accountId: string; debitAmount: string; creditAmount: string; description: string; projectId: string; partyType: string; partyId: string }[]>([
    { accountId: "", debitAmount: "", creditAmount: "", description: "", projectId: "", partyType: "", partyId: "" },
    { accountId: "", debitAmount: "", creditAmount: "", description: "", projectId: "", partyType: "", partyId: "" },
  ]);
  const [editId, setEditId] = useState<string | null>(null);

  const partyListFor = (pt: string): Array<{ id: string; name: string }> => {
    if (pt === "customer")  return customers.map((c: any) => ({ id: c.id, name: c.name }));
    if (pt === "sc")        return masterSCs.map((c: any) => ({ id: c.id, name: c.name }));
    if (pt === "supplier")  return masterSuppliers.map((c: any) => ({ id: c.id, name: c.name }));
    if (pt === "staff")     return masterStaff.map((c: any) => ({ id: c.id, name: c.name }));
    return [];
  };

  const leafAccounts = accounts.filter(a => !a.isGroup && a.isActive);

  const totalDebit = lines.reduce((s, l) => s + N(l.debitAmount), 0);
  const totalCredit = lines.reduce((s, l) => s + N(l.creditAmount), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const EMPTY_LINE = { accountId: "", debitAmount: "", creditAmount: "", description: "", projectId: "", partyType: "", partyId: "" };

  const resetForm = () => {
    setForm({ entryDate: new Date().toISOString().split("T")[0], reference: "", description: "" });
    setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
    setEditId(null);
  };

  const getAccountLabel = (id: string) => {
    const a = accounts.find(a => a.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getListJournalEntriesQueryKey({ companyId }) });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Manual double-entry journal entries</p>
        <Button size="sm" className="rounded-lg" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> New Entry
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <Card className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e: JournalEntry) => {
                const total = (e.lines || []).reduce((s: number, l: JournalEntryLine) => s + N(l.debitAmount), 0);
                return (
                  <TableRow key={e.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setViewEntry(e)}>
                    <TableCell className="text-sm">{e.entryDate}</TableCell>
                    <TableCell className="text-sm font-mono">{e.reference}</TableCell>
                    <TableCell className="text-sm">{e.description}</TableCell>
                    <TableCell>
                      <Badge variant={e.status === "posted" ? "default" : e.status === "voided" ? "destructive" : "secondary"} className="text-[10px] capitalize">
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{fc(total)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={ev => ev.stopPropagation()}>
                        {e.status === "draft" && (
                          <>
                            <Button
                              size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => {
                                setEditId(e.id);
                                setForm({ entryDate: e.entryDate, reference: e.reference || "", description: e.description || "" });
                                setLines(
                                  (e.lines || []).length > 0
                                    ? (e.lines || []).map((l: JournalEntryLine) => ({ accountId: l.accountId, debitAmount: l.debitAmount || "", creditAmount: l.creditAmount || "", description: l.description || "", projectId: (l as any).projectId || "", partyType: (l as any).partyType || "", partyId: (l as any).partyId || "" }))
                                    : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]
                                );
                                setDialogOpen(true);
                              }}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm" variant="ghost" className="h-7 px-2 text-xs text-green-600"
                              onClick={async () => {
                                try {
                                  await postMutation.mutateAsync({ id: e.id });
                                  invalidate();
                                  toast({ title: "Entry posted" });
                                } catch (err: unknown) {
                                  toast({ title: "Error", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
                                }
                              }}
                            >
                              Post
                            </Button>
                          </>
                        )}
                        {e.status === "posted" && (
                          <Button
                            size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive"
                            onClick={async () => {
                              if (!confirm("Void this entry? A reversal entry will be created.")) return;
                              try {
                                await voidMutation.mutateAsync({ id: e.id });
                                invalidate();
                                toast({ title: "Entry voided" });
                              } catch (err: unknown) {
                                toast({ title: "Error", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
                              }
                            }}
                          >
                            Void
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No journal entries yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* New / Edit Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="w-[96vw] max-w-[96vw] sm:max-w-[96vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Journal Entry" : "New Journal Entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.entryDate} onChange={e => setForm(f => ({ ...f, entryDate: e.target.value }))} className="h-9 rounded-lg" />
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} className="h-9 rounded-lg" placeholder="JE-001" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-9 rounded-lg" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">Lines</Label>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={() => setLines(l => [...l, { ...EMPTY_LINE }])}>
                  <Plus className="w-3 h-3 mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-[1.5fr_90px_90px_1fr_130px_110px_140px_32px] gap-2 items-center px-1">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Account</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Debit</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Credit</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Description</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Project</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Party Type</span>
                  <span className="text-[10px] font-medium text-muted-foreground uppercase">Party</span>
                  <span />
                </div>
                {lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-[1.5fr_90px_90px_1fr_130px_110px_140px_32px] gap-2 items-center">
                    <Select value={line.accountId || "_none"} onValueChange={v => { const nl = [...lines]; nl[i].accountId = v === "_none" ? "" : v; setLines(nl); }}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue placeholder="Account" /></SelectTrigger>
                      <SelectContent>{leafAccounts.map(a => <SelectItem key={a.id} value={a.id} className="text-xs">{a.code} — {a.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" placeholder="Debit" value={line.debitAmount} onChange={e => { const nl = [...lines]; nl[i].debitAmount = e.target.value; if (e.target.value) nl[i].creditAmount = ""; setLines(nl); }} className="h-8 rounded-lg text-xs" />
                    <Input type="number" placeholder="Credit" value={line.creditAmount} onChange={e => { const nl = [...lines]; nl[i].creditAmount = e.target.value; if (e.target.value) nl[i].debitAmount = ""; setLines(nl); }} className="h-8 rounded-lg text-xs" />
                    <Input placeholder="Desc" value={line.description} onChange={e => { const nl = [...lines]; nl[i].description = e.target.value; setLines(nl); }} className="h-8 rounded-lg text-xs" />
                    <Select value={line.projectId || "_none"} onValueChange={v => { const nl = [...lines]; nl[i].projectId = v === "_none" ? "" : v; setLines(nl); }}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue placeholder="No project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none" className="text-xs">No project</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={line.partyType || "_none"} onValueChange={v => { const nl = [...lines]; nl[i].partyType = v === "_none" ? "" : v; nl[i].partyId = ""; setLines(nl); }}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none" className="text-xs">None</SelectItem>
                        <SelectItem value="customer" className="text-xs">Customer</SelectItem>
                        <SelectItem value="sc" className="text-xs">Subcontractor</SelectItem>
                        <SelectItem value="supplier" className="text-xs">Supplier</SelectItem>
                        <SelectItem value="staff" className="text-xs">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={line.partyId || "_none"} onValueChange={v => { const nl = [...lines]; nl[i].partyId = v === "_none" ? "" : v; setLines(nl); }} disabled={!line.partyType}>
                      <SelectTrigger className="h-8 rounded-lg text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none" className="text-xs">—</SelectItem>
                        {partyListFor(line.partyType).map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {lines.length > 2 ? (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setLines(l => l.filter((_, j) => j !== i))}>
                        <X className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    ) : <span />}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 px-1">
                <div className="flex gap-4 text-xs">
                  <span>Total Debit: <strong className="font-mono">{fc(totalDebit)}</strong></span>
                  <span>Total Credit: <strong className="font-mono">{fc(totalCredit)}</strong></span>
                </div>
                <Badge variant={isBalanced ? "default" : "destructive"} className="text-[10px]">
                  {isBalanced ? "Balanced ✓" : `Difference: ${fc(Math.abs(totalDebit - totalCredit))}`}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-lg">Cancel</Button>
            <Button
              className="rounded-lg"
              disabled={!form.entryDate || lines.filter(l => l.accountId).length < 2}
              onClick={async () => {
                const cleanLines = lines.filter(l => l.accountId).map(l => ({
                  accountId: l.accountId,
                  debitAmount: l.debitAmount || "0",
                  creditAmount: l.creditAmount || "0",
                  description: l.description,
                  ...(l.projectId ? { projectId: l.projectId } : {}),
                  ...(l.partyType ? { partyType: l.partyType } : {}),
                  ...(l.partyId   ? { partyId: l.partyId }     : {}),
                }));
                try {
                  if (editId) {
                    await updateMutation.mutateAsync({ id: editId, data: { ...form, lines: cleanLines } as UpdateJournalEntryBody });
                  } else {
                    await createMutation.mutateAsync({ data: { ...form, lines: cleanLines } as CreateJournalEntryBody });
                  }
                  invalidate();
                  setDialogOpen(false);
                  resetForm();
                  toast({ title: editId ? "Entry updated" : "Entry created" });
                } catch (err: unknown) {
                  toast({ title: "Error", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
                }
              }}
            >
              Save Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Entry Dialog */}
      <Dialog open={!!viewEntry} onOpenChange={(o) => { if (!o) setViewEntry(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Journal Entry — {viewEntry?.reference || viewEntry?.id}</DialogTitle>
          </DialogHeader>
          {viewEntry && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-muted-foreground">Date:</span> {viewEntry.entryDate}</div>
                <div><span className="text-muted-foreground">Status:</span> <Badge variant={viewEntry.status === "posted" ? "default" : "secondary"} className="text-[10px] capitalize">{viewEntry.status}</Badge></div>
                <div><span className="text-muted-foreground">Ref:</span> {viewEntry.reference}</div>
              </div>
              {viewEntry.description && <p className="text-sm text-muted-foreground">{viewEntry.description}</p>}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(viewEntry.lines || []).map((l: JournalEntryLine) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs">{getAccountLabel(l.accountId)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{N(l.debitAmount) > 0 ? fc(N(l.debitAmount)) : ""}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{N(l.creditAmount) > 0 ? fc(N(l.creditAmount)) : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ GL ENTRIES */
const GL_PAGE_SIZE = 500;

function GlEntriesTab({ companyId }: { companyId: string }) {
  const [accountId, setAccountId] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [projectId, setProjectId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);

  const { data: entries = [], isLoading } = useListGlEntries({
    companyId,
    ...(accountId ? { accountId } : {}),
    ...(projectId ? { projectId } : {}),
    ...(sourceType ? { sourceType } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    limit: String(GL_PAGE_SIZE),
    offset: String(page * GL_PAGE_SIZE),
  });
  const { data: accounts = [] } = useListChartOfAccounts({ companyId });
  const { data: projects = [] } = useListProjects({ companyId });
  const leafAccounts = accounts.filter(a => !a.isGroup);

  const getAccountLabel = (id: string) => {
    const a = accounts.find(a => a.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Account</Label>
          <Select value={accountId || "_all"} onValueChange={v => { setAccountId(v === "_all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[200px] h-9 rounded-lg text-xs"><SelectValue placeholder="All accounts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all" className="text-xs">All Accounts</SelectItem>
              {leafAccounts.map(a => <SelectItem key={a.id} value={a.id} className="text-xs">{a.code} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Project</Label>
          <Select value={projectId || "_all"} onValueChange={v => { setProjectId(v === "_all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[180px] h-9 rounded-lg text-xs"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all" className="text-xs">All Projects</SelectItem>
              {(projects as Array<{ id: string; name: string }>).map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Source Type</Label>
          <Select value={sourceType || "_all"} onValueChange={v => { setSourceType(v === "_all" ? "" : v); setPage(0); }}>
            <SelectTrigger className="w-[160px] h-9 rounded-lg text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all" className="text-xs">All Types</SelectItem>
              {["journal", "client_invoice", "client_receipt", "sc_invoice", "sc_payment", "supplier_invoice", "supplier_payment", "payroll", "expense"].map(t => (
                <SelectItem key={t} value={t} className="text-xs capitalize">{t.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(0); }} className="h-9 rounded-lg w-[140px]" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(0); }} className="h-9 rounded-lg w-[140px]" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <Card className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Party</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(entries as (GlEntry & { runningBalance?: number; partyType?: string; partyId?: string; party?: { name?: string } })[]).map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs">{e.entryDate}</TableCell>
                  <TableCell className="text-xs">{getAccountLabel(e.accountId)}</TableCell>
                  <TableCell className="text-xs">{e.description}</TableCell>
                  <TableCell className="text-xs capitalize">{(e.sourceType || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {e.party?.name
                      ? <span>{e.party.name} <span className="text-[10px] opacity-60 capitalize">({(e.partyType || "").replace("sc", "SC")})</span></span>
                      : e.partyId ? <span className="opacity-50">{e.partyType}</span> : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(e.debitAmount) > 0 ? fc(N(e.debitAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(e.creditAmount) > 0 ? fc(N(e.creditAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fc(e.runningBalance || 0)}</TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No GL entries found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Showing {entries.length} entries (page {page + 1})
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-lg" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <Button size="sm" variant="outline" className="rounded-lg" disabled={entries.length < GL_PAGE_SIZE} onClick={() => setPage(p => p + 1)}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ TRIAL BALANCE */
function TrialBalanceTab({ companyId }: { companyId: string }) {
  const [startDate, setStartDate] = useState(new Date().getFullYear() + "-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const { data: rows = [], isLoading } = useGetTrialBalance({ companyId, startDate, endDate });

  const tbRows = rows as TrialBalanceRow[];
  const totalDebit = tbRows.reduce((s, r) => s + (r.periodDebit || 0), 0);
  const totalCredit = tbRows.reduce((s, r) => s + (r.periodCredit || 0), 0);

  const ACCOUNT_TYPE_ORDER = ["asset", "liability", "equity", "income", "expense"];
  const TYPE_LABELS: Record<string, string> = { asset: "Assets", liability: "Liabilities", equity: "Equity", income: "Income", expense: "Expenses" };
  const grouped = useMemo(() => {
    const groups: Record<string, TrialBalanceRow[]> = {};
    for (const r of tbRows) {
      const t = (r.accountType || "other").toLowerCase();
      if (!groups[t]) groups[t] = [];
      groups[t].push(r);
    }
    return ACCOUNT_TYPE_ORDER.filter(t => groups[t]?.length).map(t => ({ type: t, label: TYPE_LABELS[t] || t, rows: groups[t] }));
  }, [tbRows]);

  const handleExportCSV = () => {
    const header = "Account Code,Account Name,Type,Opening,Debit,Credit,Closing\n";
    const csvRows = tbRows.map((r) => `${r.accountCode},${r.accountName},${r.accountType},${r.openingBalance},${r.periodDebit},${r.periodCredit},${r.closingBalance}`);
    const blob = new Blob([header + csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `trial-balance-${startDate}-${endDate}.csv`; a.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-lg" onClick={handleExportCSV}>
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <Card className="rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Account Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Opening</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Closing</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map(g => (
                <>
                  <TableRow key={`group-${g.type}`} className="bg-muted/30">
                    <TableCell colSpan={7} className="font-semibold text-sm py-1">{g.label}</TableCell>
                  </TableRow>
                  {g.rows.map((r) => (
                    <TableRow key={r.accountId}>
                      <TableCell className="font-mono text-xs pl-6">{r.accountCode}</TableCell>
                      <TableCell className="text-sm">{r.accountName}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px] capitalize">{r.accountType}</Badge></TableCell>
                      <TableCell className="text-right font-mono text-xs">{fc(r.openingBalance)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fc(r.periodDebit)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fc(r.periodCredit)}</TableCell>
                      <TableCell className="text-right font-mono text-xs font-semibold">{fc(r.closingBalance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow key={`subtotal-${g.type}`} className="bg-muted/20 border-b-2">
                    <TableCell colSpan={4} className="text-right text-xs font-medium">{g.label} Subtotal</TableCell>
                    <TableCell className="text-right font-mono text-xs font-medium">{fc(g.rows.reduce((s, r) => s + (r.periodDebit || 0), 0))}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-medium">{fc(g.rows.reduce((s, r) => s + (r.periodCredit || 0), 0))}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-medium">{fc(g.rows.reduce((s, r) => s + (r.closingBalance || 0), 0))}</TableCell>
                  </TableRow>
                </>
              ))}
              <TableRow className="bg-muted/50 font-semibold">
                <TableCell colSpan={4} className="text-right text-sm">Totals</TableCell>
                <TableCell className="text-right font-mono text-sm">{fc(totalDebit)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fc(totalCredit)}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={Math.abs(totalDebit - totalCredit) < 0.01 ? "default" : "destructive"} className="text-[10px]">
                    {Math.abs(totalDebit - totalCredit) < 0.01 ? "Balanced ✓" : `Off by ${fc(Math.abs(totalDebit - totalCredit))}`}
                  </Badge>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ BALANCE SHEET */
function BalanceSheetTab({ companyId }: { companyId: string }) {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const { data, isLoading } = useGetBalanceSheet({ companyId, asOfDate });
  const bs = data as BalanceSheet | undefined;

  const renderSection = (title: string, items: BalanceSheetItem[], isCredit?: boolean) => (
    <div className="space-y-2">
      <h3 className="text-sm font-bold border-b border-border pb-1">{title}</h3>
      {items?.length > 0 ? items.map((item) => (
        <div key={item.accountId} className="flex justify-between text-sm px-2">
          <span>{item.code} — {item.name}</span>
          <span className="font-mono">{fc(isCredit ? Math.abs(item.balance) : item.balance)}</span>
        </div>
      )) : <p className="text-xs text-muted-foreground px-2">No entries</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div>
          <Label className="text-xs">As of Date</Label>
          <Input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="h-9 rounded-lg w-[160px]" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : bs ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-xl p-5 space-y-4">
            <h2 className="text-base font-bold">Assets</h2>
            {renderSection("Current Assets", (bs.assets || []).filter((a) => ["current_asset", "receivable", "bank", "advance"].includes(a.subtype || "")))}
            {renderSection("Fixed Assets", (bs.assets || []).filter((a) => a.subtype === "fixed_asset"))}
            {renderSection("Other Assets", (bs.assets || []).filter((a) => !["current_asset", "receivable", "bank", "advance", "fixed_asset"].includes(a.subtype || "")))}
            <div className="flex justify-between font-bold text-sm border-t border-border pt-2 px-2">
              <span>Total Assets</span>
              <span className="font-mono">{fc(bs.totalAssets || 0)}</span>
            </div>
          </Card>

          <Card className="rounded-xl p-5 space-y-4">
            <h2 className="text-base font-bold">Liabilities & Equity</h2>
            {renderSection("Current Liabilities", (bs.liabilities || []).filter((a) => ["current_liability", "payable", "tax", "accrued", "advance_receipt", "retention"].includes(a.subtype || "")), true)}
            {renderSection("Employee Liabilities", (bs.liabilities || []).filter((a) => a.subtype === "employee"), true)}
            {renderSection("Long-Term Liabilities", (bs.liabilities || []).filter((a) => a.subtype === "long_term_liability"), true)}
            <div className="flex justify-between font-semibold text-sm border-t border-border pt-2 px-2">
              <span>Total Liabilities</span>
              <span className="font-mono">{fc(bs.totalLiabilities || 0)}</span>
            </div>
            {renderSection("Equity", bs.equity || [], true)}
            <div className="flex justify-between font-semibold text-sm border-t border-border pt-2 px-2">
              <span>Total Equity</span>
              <span className="font-mono">{fc(bs.totalEquity || 0)}</span>
            </div>
            <div className="flex justify-between font-bold text-sm border-t-2 border-foreground pt-2 px-2">
              <span>Total Liabilities + Equity</span>
              <span className="font-mono">{fc(bs.totalLiabilitiesAndEquity || 0)}</span>
            </div>
            <div className="text-center mt-2">
              <Badge variant={bs.isBalanced ? "default" : "destructive"} className="text-xs">
                {bs.isBalanced ? "Balance Sheet is balanced ✓" : "Balance Sheet is NOT balanced"}
              </Badge>
            </div>
          </Card>
        </div>
      ) : <p className="text-muted-foreground">No data</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ INCOME STATEMENT */
function IncomeStatementTab({ companyId }: { companyId: string }) {
  const [startDate, setStartDate] = useState(new Date().getFullYear() + "-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const { data, isLoading } = useGetIncomeStatement({ companyId, startDate, endDate });
  const is = data as IncomeStatement | undefined;

  const renderLines = (items: IncomeStatementLineItem[]) => (
    items?.length > 0 ? items.map((item) => (
      <div key={item.accountId} className="flex justify-between text-sm px-4 py-0.5">
        <span className="text-muted-foreground">{item.code} — {item.name}</span>
        <span className="font-mono">{fc(item.amount)}</span>
      </div>
    )) : <p className="text-xs text-muted-foreground px-4">—</p>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : is ? (
        <Card className="rounded-xl p-5 max-w-2xl space-y-4">
          <h2 className="text-base font-bold text-center border-b border-border pb-2">Income Statement</h2>
          <p className="text-xs text-center text-muted-foreground">{startDate} to {endDate}</p>

          <div>
            <h3 className="text-sm font-bold text-green-600 mb-1">Direct Income</h3>
            {renderLines(is.directIncome || [])}
            <div className="flex justify-between font-semibold text-sm px-4 border-t border-border mt-1 pt-1">
              <span>Total Direct Income</span><span className="font-mono">{fc(is.totalDirectIncome || 0)}</span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-green-500 mb-1">Indirect Income</h3>
            {renderLines(is.indirectIncome || [])}
            <div className="flex justify-between font-semibold text-sm px-4 border-t border-border mt-1 pt-1">
              <span>Total Indirect Income</span><span className="font-mono">{fc(is.totalIndirectIncome || 0)}</span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-red-500 mb-1">Cost of Goods Sold</h3>
            {renderLines(is.cogsExpenses || [])}
            <div className="flex justify-between font-semibold text-sm px-4 border-t border-border mt-1 pt-1">
              <span>Total COGS</span><span className="font-mono">({fc(is.totalCogs || 0)})</span>
            </div>
          </div>

          <div className="flex justify-between font-bold text-base px-4 py-2 bg-muted rounded-lg">
            <span>Gross Profit</span>
            <span className={cn("font-mono", (is.grossProfit || 0) >= 0 ? "text-green-600" : "text-red-600")}>
              {fc(is.grossProfit || 0)}
            </span>
          </div>

          <div>
            <h3 className="text-sm font-bold text-orange-500 mb-1">Direct Expenses</h3>
            {renderLines(is.directExpenses || [])}
            <div className="flex justify-between font-semibold text-sm px-4 border-t border-border mt-1 pt-1">
              <span>Total Direct Expenses</span><span className="font-mono">({fc(is.totalDirectExpenses || 0)})</span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-red-400 mb-1">Indirect Expenses</h3>
            {renderLines(is.indirectExpenses || [])}
            <div className="flex justify-between font-semibold text-sm px-4 border-t border-border mt-1 pt-1">
              <span>Total Indirect Expenses</span><span className="font-mono">({fc(is.totalIndirectExpenses || 0)})</span>
            </div>
          </div>

          <div className="flex justify-between font-bold text-lg px-4 py-3 bg-primary/10 rounded-lg border-t-2 border-primary">
            <span>Net Profit / Loss</span>
            <span className={cn("font-mono", (is.netProfit || 0) >= 0 ? "text-green-600" : "text-red-600")}>
              {fc(is.netProfit || 0)}
            </span>
          </div>
        </Card>
      ) : <p className="text-muted-foreground">No data</p>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ ACCOUNT STATEMENT */
function AccountStatementTab({ companyId }: { companyId: string }) {
  const { data: accounts = [] } = useListChartOfAccounts({ companyId });
  const leafAccounts = accounts.filter(a => !a.isGroup);
  const [accountId, setAccountId] = useState("");
  const [startDate, setStartDate] = useState(new Date().getFullYear() + "-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);

  const { data, isLoading } = useGetAccountStatement(
    { companyId, accountId, startDate, endDate },
    { query: { enabled: !!accountId } }
  );
  const stmt = data as AccountStatement | undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Account</Label>
          <Select value={accountId || "_none"} onValueChange={v => setAccountId(v === "_none" ? "" : v)}>
            <SelectTrigger className="w-[250px] h-9 rounded-lg text-xs"><SelectValue placeholder="Select account…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" className="text-xs">Select an account…</SelectItem>
              {leafAccounts.map(a => <SelectItem key={a.id} value={a.id} className="text-xs">{a.code} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
      </div>

      {!accountId ? (
        <Card className="rounded-xl p-8 text-center text-muted-foreground">
          <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select an account to view its statement</p>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : stmt ? (
        <Card className="rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-sm">{stmt.account?.code} — {stmt.account?.name}</h3>
                <p className="text-xs text-muted-foreground capitalize">{stmt.account?.accountType} · Normal Balance: {stmt.account?.normalBalance}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Opening Balance</p>
                <p className="font-mono font-bold text-sm">{fc(stmt.openingBalance || 0)}</p>
              </div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stmt.transactions || []).map((t: GlEntry & { runningBalance?: number }) => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs">{t.entryDate}</TableCell>
                  <TableCell className="text-xs">{t.description}</TableCell>
                  <TableCell className="text-xs capitalize">{(t.sourceType || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(t.debitAmount) > 0 ? fc(N(t.debitAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(t.creditAmount) > 0 ? fc(N(t.creditAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{fc(t.runningBalance || 0)}</TableCell>
                </TableRow>
              ))}
              {(stmt.transactions || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions in this period</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex gap-6 text-xs">
                <span>Total Debit: <strong className="font-mono">{fc(stmt.totalDebit || 0)}</strong></span>
                <span>Total Credit: <strong className="font-mono">{fc(stmt.totalCredit || 0)}</strong></span>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Closing Balance</p>
                <p className="font-mono font-bold text-sm">{fc(stmt.closingBalance || 0)}</p>
              </div>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ PARTY STATEMENT */
function PartyStatementTab({ companyId }: { companyId: string }) {
  const [partyType, setPartyType] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [startDate, setStartDate] = useState(new Date().getFullYear() + "-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);

  const isCustomer = partyType === "customer";
  const isSC = partyType === "subcontractor";
  const isSupplier = partyType === "supplier";
  const isStaff = partyType === "staff";

  const { data: customers = [] } = useListCustomers({ companyId }, { query: { enabled: isCustomer } });
  const { data: masterSCs = [] } = useListMasterSCs({ companyId }, { query: { enabled: isSC } });
  const { data: masterSuppliers = [] } = useListMasterSuppliers({ companyId }, { query: { enabled: isSupplier } });
  const { data: masterStaff = [] } = useListMasterStaff({ companyId }, { query: { enabled: isStaff } });

  const partyEntityList: Array<{ id: string; name: string }> = useMemo(() => {
    if (isCustomer) return (customers as Array<{ id: string; name: string }>);
    if (isSC) return (masterSCs as Array<{ id: string; name: string }>);
    if (isSupplier) return (masterSuppliers as Array<{ id: string; name: string }>);
    if (isStaff) return (masterStaff as Array<{ id: string; name: string }>);
    return [];
  }, [isCustomer, isSC, isSupplier, isStaff, customers, masterSCs, masterSuppliers, masterStaff]);

  const selectedEntity = partyEntityList.find(e => e.id === selectedEntityId) || null;

  const { data: stmtAllData, isLoading: allLoading } = useGetPartyStatement(
    { companyId, partyType, startDate, endDate },
    { query: { enabled: !!partyType } }
  );
  const stmtAll = stmtAllData as PartyStatement | undefined;
  const partyAccounts = (stmtAll?.accounts || []) as PartyStatementAccount[];

  const matchedCoaAccountId = selectedEntity
    ? (partyAccounts.find(a => a.name === selectedEntity.name)?.id || "")
    : "";

  const { data: stmtFilteredData, isLoading: filteredLoading } = useGetPartyStatement(
    { companyId, partyType, startDate, endDate, accountId: matchedCoaAccountId },
    { query: { enabled: !!partyType && !!matchedCoaAccountId } }
  );

  const stmt = (matchedCoaAccountId ? stmtFilteredData : stmtAllData) as PartyStatement | undefined;
  const isLoading = matchedCoaAccountId ? filteredLoading : allLoading;

  const { data: coaAccounts = [] } = useListChartOfAccounts({ companyId });
  const getAccountLabel = (id: string) => {
    const a = coaAccounts.find(a => a.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };

  const handlePartyTypeChange = (v: string) => {
    setPartyType(v === "_none" ? "" : v);
    setSelectedEntityId("");
  };

  const partyTypeName = isCustomer ? "Customer" : isSupplier ? "Supplier" : isSC ? "Subcontractor" : isStaff ? "Employee" : partyType;
  const partyTypePlural = isCustomer ? "Customers" : isSupplier ? "Suppliers" : isSC ? "Subcontractors" : isStaff ? "Employees" : partyType;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Party Type</Label>
          <Select value={partyType || "_none"} onValueChange={handlePartyTypeChange}>
            <SelectTrigger className="w-[180px] h-9 rounded-lg"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">Select type…</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
              <SelectItem value="subcontractor">Subcontractor (SC)</SelectItem>
              <SelectItem value="staff">Employee / Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {partyType && (
          <div>
            <Label className="text-xs">{partyTypeName} Name</Label>
            <Select value={selectedEntityId || "_all"} onValueChange={v => setSelectedEntityId(v === "_all" ? "" : v)}>
              <SelectTrigger className="w-[240px] h-9 rounded-lg">
                <SelectValue placeholder={`All ${partyTypePlural}`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all" className="text-xs font-medium">All {partyTypePlural}</SelectItem>
                {partyEntityList.map(e => (
                  <SelectItem key={e.id} value={e.id} className="text-xs">{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 rounded-lg w-[140px]" />
        </div>
      </div>

      {!partyType ? (
        <Card className="rounded-xl p-8 text-center text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Select a party type to view their statement</p>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : stmt ? (
        <Card className="rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">
                  {selectedEntity ? selectedEntity.name : `All ${partyTypePlural}`}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedEntity
                    ? `${partyTypeName} Statement · ${startDate} to ${endDate}${matchedCoaAccountId ? "" : " · No linked GL account"}`
                    : `${partyTypeName} Statement · ${partyAccounts.length} linked account(s) · ${startDate} to ${endDate}`}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Opening Balance</p>
                <p className="font-mono font-bold text-sm">{fc(stmt.openingBalance || 0)}</p>
              </div>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stmt.transactions || []).map((t: GlEntry & { accountCode?: string; accountName?: string; runningBalance?: number }) => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs">{t.entryDate}</TableCell>
                  <TableCell className="text-xs">{t.accountCode ? `${t.accountCode} — ${t.accountName}` : getAccountLabel(t.accountId)}</TableCell>
                  <TableCell className="text-xs">{t.description}</TableCell>
                  <TableCell className="text-xs capitalize">{(t.sourceType || "").replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(t.debitAmount) > 0 ? fc(N(t.debitAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{N(t.creditAmount) > 0 ? fc(N(t.creditAmount)) : ""}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{fc(t.runningBalance || 0)}</TableCell>
                </TableRow>
              ))}
              {(stmt.transactions || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {selectedEntity && !matchedCoaAccountId
                      ? `No GL account linked to "${selectedEntity.name}" — set one up in Chart of Accounts`
                      : "No transactions in this period"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="p-4 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex gap-6 text-xs">
                <span>Total Debit: <strong className="font-mono">{fc(stmt.totalDebit || 0)}</strong></span>
                <span>Total Credit: <strong className="font-mono">{fc(stmt.totalCredit || 0)}</strong></span>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Closing Balance</p>
                <p className="font-mono font-bold text-sm">{fc(stmt.closingBalance || 0)}</p>
              </div>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
